import Button from "atoms/buttons/button";
import CultureResources from "utilities/interfaces/culture-resources";
import Icon from "atoms/icons/icon";
import OfflineAlertModal from "organisms/modals/offline-alert-modal/offline-alert-modal";
import React from "react";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import useBuildReferencePanel from "utilities/hooks/reference-panel/use-build-reference-panel";
import useModalActions from "utilities/hooks/use-modal-actions";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import useReferencePanel from "utilities/hooks/reference-panel/use-reference-panel";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { t } from "utilities/localization-utils";
import { useBookviewTabPanel } from "utilities/contexts/bookview-tab-panel/use-bookview-tab-panel";
import { useMobileSecondaryNavContent } from "utilities/contexts/use-mobile-secondary-nav/use-mobile-secondary-nav-content";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export interface ReferencePanelButtonProps {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const CSS_BASE_CLASS_NAME = "c-reference-panel-button";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ReferencePanelButton: React.FC<ReferencePanelButtonProps> = (
    props: ReferencePanelButtonProps
) => {
    const { isOpen, userReferences } = useReferencePanel();
    const { toggleReferencePanel } = useBookviewTabPanel();
    const { toggleReferenceModal } = useMobileSecondaryNavContent();
    const { isOnline } = useNetworkInformation();
    const isMobile = useBreakpoint(
        Breakpoints.Tablet,
        BreakpointComparer.MaxWidth
    );
    const { inMobileView: showMobileNav } = useBreakpointRanges();
    const {
        isOpen: isOfflineAlertModalOpen,
        handleClose: handleOfflineAlertModalClose,
        handleOpen: handleFeatureUnavailableModalOpen,
    } = useModalActions();

    // Build reference state for counts
    useBuildReferencePanel();

    const action: keyof CultureResources = isOpen ? "close" : "open";
    const title = t("reference-panel-menu", { action: t(action) });

    const cssModifiers: string[] = [];
    if (isOpen) {
        cssModifiers.push("-panel-open");
    }

    if (!isOnline) {
        cssModifiers.push("-offline");
    }

    const userReferencesCountClassName = [`${CSS_BASE_CLASS_NAME}__counter`];
    const userReferencesCount = userReferences.count();
    if (userReferencesCount > 0) {
        userReferencesCountClassName.push("-has-items");
    }

    const offlineTooltipContent = (
        <div>
            <div className={`${CSS_BASE_CLASS_NAME}__tooltip__title`}>
                {t("reference_plural")}
            </div>
            <div className={`${CSS_BASE_CLASS_NAME}__tooltip__description`}>
                {t("offline-fallback-description")}
            </div>
        </div>
    );

    const handleToggleClick = () => {
        if (isOnline) {
            toggleReferencePanel();
            return;
        }

        if (!isMobile) {
            return;
        }

        handleFeatureUnavailableModalOpen();
    };

    return (
        <div
            className={CSS_BASE_CLASS_NAME}
            data-test-id={"reference-panel-button"}>
            {showMobileNav ? (
                <Button
                    onClick={toggleReferenceModal}
                    data-prevent-menu-auto-close="true">
                    <Icon
                        size={IconSizes.Large}
                        type={Icons.PushPin}
                        cssClassName={`${CSS_BASE_CLASS_NAME}__icon`}
                    />
                    <span className={`${CSS_BASE_CLASS_NAME}__text`}>
                        {t("reference_plural")} ({userReferencesCount})
                    </span>
                </Button>
            ) : (
                <Tooltip
                    content={offlineTooltipContent}
                    cssClassName={`${CSS_BASE_CLASS_NAME}__tooltip`}
                    disabled={isMobile || isOnline}
                    durationInMs={0}
                    hideOnClick={true}
                    placement={TooltipPlacement.Bottom}>
                    <Button
                        accessibleText={title}
                        ariaDisabled={!isOnline}
                        cssClassName={`${CSS_BASE_CLASS_NAME}__container ${cssModifiers.join(
                            " "
                        )}`}
                        onClick={handleToggleClick}
                        size={ButtonSizes.Small}
                        style={ButtonStyles.TertiaryAlt}>
                        <div className={userReferencesCountClassName.join(" ")}>
                            <span>{userReferencesCount}</span>
                        </div>
                        <Icon
                            size={IconSizes.Large}
                            type={Icons.PushPin}
                            cssClassName={`${CSS_BASE_CLASS_NAME}__icon`}
                        />
                    </Button>
                </Tooltip>
            )}
            {isOfflineAlertModalOpen && (
                <OfflineAlertModal
                    isOpen={isOfflineAlertModalOpen}
                    handleClose={handleOfflineAlertModalClose}
                />
            )}
        </div>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default ReferencePanelButton;

// #endregion Exports
